import React, { FC, useState } from 'react'
import { Grid, Paper } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CollapsibleSection,
  ContractorTransferActions,
  ContractorTransferConflictList,
  ContractorTransferCounts,
  ContractorTransferErrors,
  ContractorTransferInfo,
  ContractorTransferItems,
  ContractorTransferStages,
  DataLoad,
  EntityHeader,
  HasSidebarLayout
} from 'src/components'
import { getTransferErrorRows, getTransferRefetchInterval, TransferRetryMark } from 'src/components/columns/contractorTransferHelpers'
import { useContractorTransferRequest } from 'src/hooks/api'

const ContractorTransferShow: FC = () => {
  const params = useParams()
  const id = parseInt((params as { transferId: string }).transferId, 10)
  document.title = `CLīMIT Admin Panel - Transfer #${id}`
  const navigate = useNavigate()
  const [retry, setRetry] = useState<TransferRetryMark | null>(null)
  const { data, isInitialLoading, isError, error } = useContractorTransferRequest({
    id,
    options: {
      enabled: Number.isInteger(id),
      // polls every 5 s until the status is final (or a retry is picked up), also while the tab is in the background
      refetchInterval: (transfer) => getTransferRefetchInterval(transfer, retry),
      refetchIntervalInBackground: true
    }
  })

  const warnings = data?.warnings || []
  const items = data?.items || []
  const counts = data?.counts || {}
  const errorRows = data ? getTransferErrorRows(data) : []
  const exportPrefix = `${document.title}`

  return (
    <HasSidebarLayout>
      <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message}>
        {data ? (
          <Grid container>
            <Grid item xs={12}>
              <EntityHeader
                title={`Transfer #${data.id}`}
                onBack={() => navigate('/settings?tab=Transfer')}
                actionBlockOnTheRight={<ContractorTransferActions transfer={data} onRetried={() => setRetry({ updatedAt: data.updatedAt, at: Date.now() })} />}
              />
            </Grid>
            <Grid item xs={12}>
              <ContractorTransferInfo transfer={data} />
            </Grid>
            <Grid item xs={12} mt={3} minWidth={0}>
              <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
                <ContractorTransferStages transfer={data} />
              </Paper>
            </Grid>
            <Grid item xs={12} mt={2} minWidth={0}>
              <CollapsibleSection title={`Errors by stage (${errorRows.length})`} defaultExpanded={errorRows.length > 0}>
                <ContractorTransferErrors rows={errorRows} exportedFileName={`${exportPrefix}_errors`} />
              </CollapsibleSection>
            </Grid>
            <Grid item xs={12} minWidth={0}>
              <CollapsibleSection title={`Warnings (${warnings.length})`} defaultExpanded={warnings.length > 0}>
                <ContractorTransferConflictList conflicts={warnings} exportedFileName={`${exportPrefix}_warnings`} />
              </CollapsibleSection>
            </Grid>
            <Grid item xs={12} minWidth={0}>
              <CollapsibleSection title={`Moved items (${items.length})`}>
                <ContractorTransferItems items={items} exportedFileName={`${exportPrefix}_items`} />
              </CollapsibleSection>
            </Grid>
            <Grid item xs={12} minWidth={0}>
              <CollapsibleSection title="Rows changed">
                <ContractorTransferCounts counts={counts} exportedFileName={`${exportPrefix}_rows`} />
              </CollapsibleSection>
            </Grid>
          </Grid>
        ) : null}
      </DataLoad>
    </HasSidebarLayout>
  )
}

export default ContractorTransferShow
